import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getPathMemoryParams,
  generatePath,
  type PathMemoryParams,
} from "./pathMemoryContent";
import {
  gameContinueButtonSx,
  gameSecondaryButtonSx,
} from "@components/Home/Games/gameUi";

const CELL_SIZE_DESKTOP = 88;
const CELL_SIZE_MOBILE = 84;
const GRID_GAP_DESKTOP = 12;
const GRID_GAP_MOBILE = 6;
const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const PAUSE_BEFORE_RECALL_MS = 1000;

let lastSessionStartAt = 0;

type Phase = "idle" | "showing" | "recall" | "feedback";

export interface PathMemoryGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

function computeRoundMetrics(
  correctPath: number[],
  userPath: number[]
): { accuracy: number; longestCorrectPath: number; firstErrorIndex: number; errorCount: number } {
  const pathLength = correctPath.length;
  if (pathLength === 0) {
    return { accuracy: 1, longestCorrectPath: 0, firstErrorIndex: 0, errorCount: 0 };
  }
  let longestCorrectPath = 0;
  let firstErrorIndex = 0;
  let correctCount = 0;
  for (let i = 0; i < pathLength && i < userPath.length; i++) {
    if (userPath[i] === correctPath[i]) {
      correctCount++;
      longestCorrectPath = i + 1;
    } else {
      if (firstErrorIndex === 0) firstErrorIndex = i + 1;
    }
  }
  const errorCount = pathLength - correctCount;
  const accuracy = correctCount / pathLength;
  return { accuracy, longestCorrectPath, firstErrorIndex, errorCount };
}

const PathMemory: FC<PathMemoryGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const contentBoxRef = useRef<HTMLDivElement | null>(null);
  const [contentBoxSize, setContentBoxSize] = useState({ width: 0, height: 0 });
  const isMobile = useMediaQuery("(max-width: 600px)");
  const fallbackCellSize = isMobile ? CELL_SIZE_MOBILE : CELL_SIZE_DESKTOP;
  const gridGap = isMobile ? GRID_GAP_MOBILE : GRID_GAP_DESKTOP;

  useEffect(() => {
    const el = contentBoxRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      setContentBoxSize({ width: cr.width, height: cr.height });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sessionStartedRef = useRef(false);

  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [params, setParams] = useState<PathMemoryParams | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [correctPath, setCorrectPath] = useState<number[]>([]);
  const [userPath, setUserPath] = useState<number[]>([]);
  const [showIndex, setShowIndex] = useState(0);
  const [showRepeatCount, setShowRepeatCount] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [roundCount, setRoundCount] = useState(0);
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ correct: boolean; completionMs: number }[]>([]);
  const [lastRoundCompletionMs, setLastRoundCompletionMs] = useState(0);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);

  /** Increments each time we generate a path so every round gets a different seed. */
  const pathRoundRef = useRef(0);

  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const contentSeed = effectiveGame?.contentSeed;

  const startNewRound = useCallback(() => {
    const levelParams = getPathMemoryParams(difficulty);
    setParams(levelParams);
    const index = pathRoundRef.current;
    pathRoundRef.current += 1;
    const roundSeed = contentSeed ? `${contentSeed}-${index}` : `path-${Date.now()}-${index}`;
    const path = generatePath(
      levelParams.gridRows,
      levelParams.gridCols,
      levelParams.pathLength,
      levelParams.allowBacktrack,
      roundSeed
    );
    setCorrectPath(path);
    setUserPath([]);
    setShowIndex(0);
    setShowRepeatCount(0);
    setPhase("showing");
    setRoundStartTime(0);
    setXpEarned(null);
    setFeedbackCorrect(false);
  }, [difficulty, contentSeed]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  const displayMs = params?.displayMs ?? 800;
  const showCount = params?.showCount ?? 1;

  useEffect(() => {
    if (phase !== "showing" || !params || correctPath.length === 0) return;
    if (showIndex < correctPath.length) {
      const t1 = window.setTimeout(() => setShowIndex((i) => i + 1), displayMs);
      return () => clearTimeout(t1);
    }
    if (showRepeatCount + 1 < showCount) {
      const t2 = window.setTimeout(() => {
        setShowIndex(0);
        setShowRepeatCount((c) => c + 1);
      }, 300);
      return () => clearTimeout(t2);
    }
    const t3 = window.setTimeout(() => {
      setPhase("recall");
      setRoundStartTime(Date.now());
    }, PAUSE_BEFORE_RECALL_MS);
    return () => clearTimeout(t3);
  }, [phase, showIndex, correctPath.length, params, displayMs, showCount, showRepeatCount]);

  const handleCellTap = useCallback(
    (cellIndex: number) => {
      if (phase !== "recall" || !params) return;
      if (userPath.length >= correctPath.length) return;
      const next = [...userPath, cellIndex];
      setUserPath(next);
      if (next.length === correctPath.length) {
        const completionTimeMs = Date.now() - roundStartTime;
        const { accuracy, longestCorrectPath, firstErrorIndex, errorCount } =
          computeRoundMetrics(correctPath, next);
        const correct = accuracy === 1;
        setFeedbackCorrect(correct);
        setLastRoundCompletionMs(completionTimeMs);
        setPhase("feedback");
        setRoundResults((prev) => [...prev, { correct, completionMs: completionTimeMs }]);

        if (sessionId) {
          submitRound({
            sessionId,
            body: {
              gameId: GAME_IDS.pathMemory,
              roundNumber: roundCount + 1,
              difficulty,
              accuracy,
              completionTimeMs,
              errorCount,
              rawData: {
                correctPath: [...correctPath],
                userPath: next,
                firstErrorIndex,
                longestCorrectPath,
              },
            },
          })
            .unwrap()
            .then((res) => {
              if (res?.xpEarned != null) {
                setXpEarned(res.xpEarned);
                setSessionScore((s) => s + res.xpEarned);
              }
              if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
                setDomainUpdatesSum((prev) => {
                  const nextSum = { ...prev };
                  for (const [k, v] of Object.entries(res.domainUpdates)) {
                    nextSum[k] = (nextSum[k] ?? 0) + v;
                  }
                  return nextSum;
                });
              }
            })
            .catch(() => {});
        } else {
          const xp = correct ? 20 : 10;
          setXpEarned(xp);
          setSessionScore((s) => s + xp);
        }
      }
    },
    [
      phase,
      params,
      userPath,
      correctPath,
      roundStartTime,
      sessionId,
      submitRound,
      roundCount,
      difficulty,
    ]
  );

  const handleUndo = useCallback(() => {
    if (phase === "recall" && userPath.length > 0) {
      setUserPath((prev) => prev.slice(0, -1));
    }
  }, [phase, userPath.length]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    setRoundCount(nextRound);
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allCorrect = [...roundResults.map((r) => r.correct), feedbackCorrect];
      const allCompletion = [...roundResults.map((r) => r.completionMs), lastRoundCompletionMs];
      const totalRounds = allCorrect.length;
      const avgCompletionMs =
        totalRounds > 0
          ? allCompletion.reduce((a, b) => a + b, 0) / totalRounds
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: (allCorrect.filter(Boolean).length / totalRounds) * 100,
              avgReactionSeconds: avgCompletionMs / 1000,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: (allCorrect.filter(Boolean).length / totalRounds) * 100,
              avgReactionSeconds: avgCompletionMs / 1000,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: (allCorrect.filter(Boolean).length / totalRounds) * 100,
          avgReactionSeconds: avgCompletionMs / 1000,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    startNewRound();
  }, [
    roundCount,
    sessionId,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
    roundResults,
    feedbackCorrect,
    lastRoundCompletionMs,
    sessionScore,
    domainUpdatesSum,
  ]);

  const handleReplayPath = useCallback(() => {
    setPhase("showing");
    setShowIndex(0);
    setShowRepeatCount(0);
    setUserPath([]);
  }, []);

  const handleExit = useCallback(() => {
    if (sessionId) {
      completeSession({ sessionId }).catch(() => {});
    }
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const highlightedCell =
    phase === "showing" && showIndex < correctPath.length
      ? correctPath[showIndex]
      : null;

  const cellCount = params ? params.gridRows * params.gridCols : 0;
  const dotsVisible =
    (phase === "recall" || phase === "feedback") && correctPath.length > 0;
  const dotsHeightPx = 14;
  const reservedActionHeight = 170;
  const columnGapPx = 16;

  const cellSize = useMemo(() => {
    if (!params) return fallbackCellSize;
    if (contentBoxSize.width <= 0 || contentBoxSize.height <= 0) {
      return fallbackCellSize;
    }

    const maxCellWidth = Math.floor(
      (contentBoxSize.width - gridGap * (params.gridCols - 1)) / params.gridCols,
    );

    const availableHeightForGrid = Math.max(
      0,
      contentBoxSize.height -
        reservedActionHeight -
        (dotsVisible ? dotsHeightPx : 0) -
        columnGapPx * 2,
    );
    const maxCellHeight = Math.floor(
      (availableHeightForGrid - gridGap * (params.gridRows - 1)) / params.gridRows,
    );
    const computed =
      maxCellHeight > 0
        ? Math.floor(Math.min(maxCellWidth, maxCellHeight))
        : Math.floor(maxCellWidth);

    const preferredMinCell = 48;
    const hardMinCell = 32;
    const preferredSized = Math.max(preferredMinCell, computed);
    if (maxCellWidth <= hardMinCell) return Math.max(1, maxCellWidth);
    return Math.max(hardMinCell, Math.min(maxCellWidth, preferredSized));
  }, [
    params,
    contentBoxSize.width,
    contentBoxSize.height,
    gridGap,
    dotsVisible,
    fallbackCellSize,
  ]);

  const feedbackText =
    phase === "showing"
      ? t("games.pathMemory.watch")
      : phase === "recall"
        ? t("games.pathMemory.tapPath", { count: correctPath.length })
        : phase === "feedback"
          ? feedbackCorrect
            ? t("games.pathMemory.feedbackCorrect")
            : t("games.pathMemory.feedbackIncorrect")
          : null;

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.pathMemory.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.pathMemory.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.pathMemory.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            sx={gameContinueButtonSx}
          >
            {t("games.pathMemory.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "showing" || phase === "recall" || phase === "feedback") &&
        params && (
          <Box
            ref={contentBoxRef}
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${params.gridCols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${params.gridRows}, minmax(0, 1fr))`,
              gap: `${gridGap}px`,
              width: `min(100%, calc(100vw - 24px), ${params.gridCols * cellSize + gridGap * (params.gridCols - 1)}px)`,
              aspectRatio: `${params.gridCols} / ${params.gridRows}`,
              margin: "0 auto",
              flexShrink: 0,
            }}
          >
            {Array.from({ length: cellCount }, (_, i) => {
              const isHighlighted = highlightedCell === i;
              const userTapIndex = userPath.indexOf(i);
              const isCorrectTap = userTapIndex >= 0 && correctPath[userTapIndex] === i;
              const isWrongTap = userTapIndex >= 0 && correctPath[userTapIndex] !== i;
              const isCorrectPathCell = phase === "feedback" && correctPath.includes(i);
              const isUserPathCell = phase === "feedback" && userPath.includes(i);

              let borderColor = "divider";
              let bgColor = "background.paper";
              if (phase === "showing") {
                if (isHighlighted) {
                  borderColor = "primary.main";
                  bgColor = "primary.main";
                }
              } else if (phase === "recall") {
                if (userTapIndex >= 0) {
                  borderColor = isCorrectTap ? "success.main" : "warning.main";
                  bgColor = isCorrectTap ? "success.light" : "warning.light";
                }
              } else if (phase === "feedback") {
                if (isCorrectPathCell && !isUserPathCell) {
                  borderColor = "info.main";
                  bgColor = "info.light";
                } else if (isUserPathCell && !isCorrectPathCell) {
                  borderColor = "warning.main";
                  bgColor = "warning.light";
                } else if (isCorrectPathCell && isUserPathCell) {
                  const idx = userPath.indexOf(i);
                  const correct = idx >= 0 && userPath[idx] === correctPath[idx];
                  borderColor = correct ? "success.main" : "warning.main";
                  bgColor = correct ? "success.light" : "warning.light";
                }
              }

              return (
                <Box
                  key={i}
                  onClick={() => handleCellTap(i)}
                  sx={{
                    width: "100%",
                    height: "100%",
                    aspectRatio: "1 / 1",
                    boxSizing: "border-box",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor,
                    backgroundColor: bgColor,
                    cursor: phase === "recall" ? "pointer" : "default",
                    transition: "background-color 0.2s, border-color 0.2s, box-shadow 0.2s",
                    boxShadow: isHighlighted
                      ? (th) => `0 0 0 4px ${th.palette.primary.main}40`
                      : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              );
            })}
          </Box>

          {(phase === "recall" || phase === "feedback") && correctPath.length > 0 && (
            <Box
              display="flex"
              justifyContent="center"
              gap={0.75}
              flexShrink={0}
            >
              {Array.from({ length: correctPath.length }, (_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor:
                      i < userPath.length
                        ? userPath[i] === correctPath[i]
                          ? "success.main"
                          : "warning.main"
                        : "action.hover",
                    transition: "background-color 0.2s",
                  }}
                  aria-hidden
                />
              ))}
            </Box>
          )}

          <Box
            height={120}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            flexShrink={0}
          >
            {phase === "recall" && userPath.length > 0 && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleUndo}
                sx={gameSecondaryButtonSx}
              >
                {t("games.pathMemory.undo")}
              </Button>
            )}
            {phase === "feedback" && (
              <>
                {xpEarned != null && (
                  <Typography variant="body2" color="primary.main">
                    +{xpEarned} XP
                  </Typography>
                )}
                {!feedbackCorrect && params.showCount >= 2 && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleReplayPath}
                    sx={gameSecondaryButtonSx}
                  >
                    {t("games.pathMemory.seePathAgain")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNextRound}
                  sx={{ ...gameContinueButtonSx, mb: 4 }}
                >
                  {roundCount + 1 >= ROUNDS_PER_SESSION
                    ? t("games.pathMemory.finishSession")
                    : t("games.pathMemory.nextRound")}
                </Button>
              </>
            )}
          </Box>
          </Box>
      )}
    </GameScreenShell>
  );
};

export default PathMemory;
